import { CancelOutlined, ExpandMore, HowToRegOutlined, MarkEmailReadOutlined, PictureAsPdfOutlined } from "@mui/icons-material";
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import React from "react";
import { useProfile } from "../../../context/ProfileContext";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { useToast } from "../../../shared/hooks";
import { IAgreement, IAgreementInstallment, SinisterAgreementService } from "../../../shared/services/api/sinister/SinisterAgreementService";
import { ModalBulkCancelInstallments } from "./Modals/ModalBulkCancelInstallments";
import { ModalBulkCreateBillet } from "./Modals/ModalBulkCreateBillet";
import { ModalBulkManualSettleInstallments } from "./Modals/ModalBulkManualSettleInstallments";

interface Props {
	agreement: IAgreement;
	installments: IAgreementInstallment[];
	onChanged: () => void;
}

export function InstallmentBulkActionsMenu({ agreement, installments, onChanged }: Props) {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [openManualSettleModal, setOpenManualSettleModal] = React.useState(false);
	const [openCancelModal, setOpenCancelModal] = React.useState(false);
	const [openCreateBilletModal, setOpenCreateBilletModal] = React.useState(false);
	const open = Boolean(anchorEl);
	const { isSuperAdmin } = useProfile();
	const toastHandler = useToast();

	const isLegal = agreement.sector === "juridico";
	const isAgreementCanceled = agreement.status === "broken";
	const canManualSettle =
		(isSuperAdmin ||
			checkByProfileGuard(
				PermissionRolesEnum.AgreementsOptions,
				isLegal ? PermissionActionsEnum.ManualSettleInstallmentLegal : PermissionActionsEnum.ManualSettleInstallmentCollection
			)) &&
		!isAgreementCanceled;
	const canCancel =
		(isSuperAdmin ||
			checkByProfileGuard(
				PermissionRolesEnum.AgreementsOptions,
				isLegal ? PermissionActionsEnum.CancelInstallmentLegal : PermissionActionsEnum.CancelInstallmentCollection
			)) &&
		!isAgreementCanceled;
	const canCreateBillet =
		(isSuperAdmin ||
			checkByProfileGuard(
				PermissionRolesEnum.AgreementsOptions,
				isLegal ? PermissionActionsEnum.ReissueInstallmentLegal : PermissionActionsEnum.ReissueInstallmentCollection
			)) &&
		!isAgreementCanceled;
	const canViewPdf =
		isSuperAdmin ||
		checkByProfileGuard(
			PermissionRolesEnum.AgreementsOptions,
			isLegal ? PermissionActionsEnum.ReissueInstallmentLegal : PermissionActionsEnum.ReissueInstallmentCollection
		);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
	const handleClose = () => setAnchorEl(null);

	// Sem endpoint de PDF em lote no backend — abre um PDF por vez (window.open), na ordem. Navegadores
	// podem bloquear parte dos pop-ups quando são vários de uma vez; o usuário pode precisar autorizar.
	const handleViewPdfBulk = async () => {
		const withPdf = installments.filter((installment) => !!installment.id);
		if (withPdf.length === 0) {
			toastHandler.present({
				type: "error",
				messageOrContent: "Nenhuma das parcelas selecionadas possui PDF de boleto disponível.",
				position: "top-right",
			});
			return;
		}

		for (const installment of withPdf) {
			// eslint-disable-next-line no-await-in-loop
			await SinisterAgreementService.getBoletoPdf(installment.id!);
		}

		const skipped = installments.length - withPdf.length;
		toastHandler.present({
			type: skipped > 0 ? "warning" : "success",
			messageOrContent:
				skipped > 0
					? `${withPdf.length} PDF(s) aberto(s). ${skipped} parcela(s) sem PDF disponível foram ignoradas.`
					: `${withPdf.length} PDF(s) aberto(s) em novas abas.`,
			position: "top-right",
		});
	};

	return (
		<>
			<Button size="small" variant="outlined" endIcon={<ExpandMore />} disabled={installments.length === 0} onClick={handleClick}>
				Ações em Massa {installments.length > 0 ? `(${installments.length})` : ""}
			</Button>

			<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
				<Tooltip title={canManualSettle ? "" : "Sem permissão ou acordo indisponível"} placement="right">
					<span>
						<MenuItem
							disabled={!canManualSettle}
							onClick={() => {
								handleClose();
								setOpenManualSettleModal(true);
							}}
						>
							<ListItemIcon>
								<HowToRegOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Baixa Manual em Massa</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canCancel ? "" : "Sem permissão ou acordo indisponível"} placement="right">
					<span>
						<MenuItem
							disabled={!canCancel}
							onClick={() => {
								handleClose();
								setOpenCancelModal(true);
							}}
						>
							<ListItemIcon>
								<CancelOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Cancelar Parcelas</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canCreateBillet ? "" : "Sem permissão ou acordo indisponível"} placement="right">
					<span>
						<MenuItem
							disabled={!canCreateBillet}
							onClick={() => {
								handleClose();
								setOpenCreateBilletModal(true);
							}}
						>
							<ListItemIcon>
								<MarkEmailReadOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Gerar/Reenviar Boletos</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>

				<Tooltip title={canViewPdf ? "" : "Sem permissão"} placement="right">
					<span>
						<MenuItem
							disabled={!canViewPdf}
							onClick={() => {
								handleClose();
								handleViewPdfBulk();
							}}
						>
							<ListItemIcon>
								<PictureAsPdfOutlined fontSize="small" />
							</ListItemIcon>
							<ListItemText>Visualizar Boletos (PDF)</ListItemText>
						</MenuItem>
					</span>
				</Tooltip>
			</Menu>

			<ModalBulkManualSettleInstallments
				open={openManualSettleModal}
				agreementId={agreement.id}
				installments={installments}
				onClose={() => setOpenManualSettleModal(false)}
				onConfirm={() => {
					setOpenManualSettleModal(false);
					onChanged();
				}}
			/>
			<ModalBulkCancelInstallments
				open={openCancelModal}
				agreementId={agreement.id}
				installments={installments}
				onClose={() => setOpenCancelModal(false)}
				onConfirm={() => {
					setOpenCancelModal(false);
					onChanged();
				}}
			/>
			<ModalBulkCreateBillet
				open={openCreateBilletModal}
				agreementId={agreement.id}
				installments={installments}
				onClose={() => setOpenCreateBilletModal(false)}
				onConfirm={() => {
					setOpenCreateBilletModal(false);
					onChanged();
				}}
			/>
		</>
	);
}
